import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material'
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import EmptyState from '@/components/shared/EmptyState'
import { PageHeader, CodeBlock } from '@/components/styled'
import type { Backend, Account } from '@/types'

interface Props {
  backends: Backend[]
}

const serviceColors: Record<string, 'warning' | 'error' | 'default'> = {
  aws: 'warning',
  minio: 'error',
  other: 'default',
}

const serviceLabels: Record<string, string> = {
  aws: 'AWS S3',
  minio: 'MinIO',
  other: 'S3-Compatible',
}

export default function BackendsIndex({ backends }: Props) {
  const { currentAccount, isAccountAdmin } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
  }>().props

  return (
    <ManagementLayout>
      <PageHeader
        title="Backends"
        description="Storage backends connected to this account."
        action={
          isAccountAdmin && (
            <Button
              component={Link}
              href={`/accounts/${currentAccount.slug}/backends/new`}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Add Backend
            </Button>
          )
        }
      />

      {backends.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Virtual Buckets</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backends.map((backend) => (
                <TableRow key={backend.id} hover>
                  <TableCell>
                    <Link href={`/accounts/${currentAccount.slug}/backends/${backend.slug}`}>
                      {backend.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {backend.backend_type === 'local' ? (
                      <Chip label="Local" size="small" color="secondary" />
                    ) : (
                      <Chip
                        label={serviceLabels[backend.service] || 'S3-Compatible'}
                        size="small"
                        color={serviceColors[backend.service] || 'default'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {backend.backend_type === 'local' ? (
                      <CodeBlock>{backend.storage_path || '-'}</CodeBlock>
                    ) : (
                      <>
                        <CodeBlock>{backend.bucket}</CodeBlock>
                        {backend.region && (
                          <span style={{ marginLeft: 8, color: '#888' }}>
                            ({backend.region})
                          </span>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell>{backend.virtualBucketsCount ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      component={Link}
                      href={`/accounts/${currentAccount.slug}/backends/${backend.slug}`}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {isAccountAdmin && (
                      <IconButton
                        component={Link}
                        href={`/accounts/${currentAccount.slug}/backends/${backend.slug}/edit`}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon="server"
          title="No backends"
          description="Connect your first storage backend."
          actionPath={isAccountAdmin ? `/accounts/${currentAccount.slug}/backends/new` : undefined}
          actionText={isAccountAdmin ? 'Add Backend' : undefined}
        />
      )}
    </ManagementLayout>
  )
}
